<template>
  <div>
    <Transition name="modal-fade">
      <RiskProfileModal v-if="showRiskModal" @cancel="showRiskModal = false" :open="showRiskModal"/>
    </Transition>
    <HighRiskConfirmationDialog
      v-if="requireHighRiskConfirmation"
      v-model="showHighRiskDialog"
      :amlOfficerEmail="organization?.attributes?.amlOfficerEmail"
      @confirm="onHighRiskConfirm"
      @cancel="showHighRiskDialog = false"
    />
    <div v-if="riskScoring">
      <div class="px-4 py-5 sm:px-6">
        <span class="text-m font-semibold text-gray-900">{{ $gettext("Profil de risque") }}</span>
      </div>
      <div class="px-4 py-2 sm:px-6 flex items-center justify-between">
        <div>
          <span class="text-m font-light text-gray-600">{{ $gettext("Dernière mise à jour :") }}</span>
          <span class="ml-2 text-m font-light text-gray-600">
            {{ $formatDateTimeLong(riskScoring.attributes.updatedAt) }}
          </span>
        </div>
      </div>
      <div class="h-40 w-full flex flex-col justify-center p-2">
        <RiskScoringLabel
          cssClasses="h-20 w-full flex items-center justify-center font-bold"
          :level="riskScoring.attributes.scoreLevel"
        />
        <div class="flex justify-between items-center w-full mt-2">
          <div class="flex space-x-2">
            <button
              v-if="!riskScoring.attributes.reviewApproval"
              @click="confirmRiskScoring()"
              :disabled="hasChanges || confirming"
              :class="[
                'inline-flex items-center rounded-md px-3 py-2 text-sm font-semibold shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
                !hasChanges && !confirming
                  ? 'bg-green-600 text-white hover:bg-green-500 focus-visible:outline-green-600'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              ]"
            >
              <CheckCircleIcon :class="['h-5 w-5 mr-1', !hasChanges ? 'text-white' : 'text-gray-300']" aria-hidden="true" />
              {{ $gettext("Confirmer le profil de risque") }}
            </button>

            <span
              v-else
              class="inline-flex items-center px-3 py-1 rounded-md bg-green-100 text-green-700 text-sm font-medium"
            >
              <CheckCircleIcon class="h-5 w-5 mr-2 text-green-600" />
              <span>{{ $gettext("Profil de risque confirmé") }}</span>
            </span>
          </div>
          <button
            @click="recomputeRiskScoring()"
            :disabled="false"
            :class="[
              'inline-flex items-center rounded-md px-3 py-2 text-sm font-semibold shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
              true
                ? 'bg-indigo-600 text-white hover:bg-indigo-500 focus-visible:outline-indigo-600'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            ]"
          >
            <XCircleIcon class="h-5 w-5 mr-1" aria-hidden="true" />
            {{ $gettext("Recalculer le profil de risque") }}
          </button>
        </div>
      </div>
      <div class="px-4 py-5 sm:px-6">
        <span class="text-m font-semibold text-gray-900">{{ $gettext("Détail du calcul") }}</span>
      </div>
      <div class="px-4 py-5 sm:p-6">
        <div v-for="riskScoringRuleExecution in riskScoringRuleExecutions" :key="riskScoringRuleExecution.id" class="mb-4">
          <RiskScoringFactorField
            v-if="riskScoringRuleExecution.relationships.riskScoringRule.data.attributes.criticality != 'sanction' || riskScoringRuleExecution.attributes.level === 'high'"
            :id="riskScoringRuleExecution.id"
            :score="riskScoringRuleExecution.attributes.score"
            :level="riskScoringRuleExecution.attributes.level"
            :message="riskScoringRuleExecution.attributes.justification"
            :criticality="riskScoringRuleExecution.relationships.riskScoringRule.data.attributes.criticality"
            v-model:skipped="riskScoringRuleExecution.attributes.skipped"
            v-model:justification="riskScoringRuleExecution.attributes.skipReason"
            :error="validationErrors[riskScoringRuleExecution.id]"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import RiskScoringLabel from "@/components/risk-scorings/RiskScoringLabel.vue";
import RiskScoringFactorField from "@/components/form-fields/RiskScoringFactorField.vue";
import { computed, ref, watch, onMounted } from "vue";
import { CheckCircleIcon, XCircleIcon } from "@heroicons/vue/24/outline";
import { useRoute } from "vue-router";
import RiskProfileModal from "@/components/risk-scorings/RiskProfileModal.vue";
import HighRiskConfirmationDialog from "@/components/risk-scorings/HighRiskConfirmationDialog.vue";
import { useOrganizationStore } from "@/stores/organization.js";

const emit = defineEmits(["updated"]);

const props = defineProps({
  riskScoringStore: Object,
  riskScoring: Object,
  requireHighRiskConfirmation: { type: Boolean, default: false },
  organization: { type: Object, default: null }
});

const showHighRiskDialog = ref(false);
const organizationStore = useOrganizationStore();

const route = useRoute();
const riskScoringStore = props.riskScoringStore;
const riskScoringOwnerId = route.params.id;
const baseRiskScoringString = ref(null);
const showRiskModal = ref(false);
const hasChanges = ref(false);
const confirming = ref(false);
const validationErrors = ref({});

const riskScoringRuleExecutions = computed(() => {
  return riskScoring.value?.relationships?.riskScoringRuleExecutions?.data;
});


const riskScoring = computed(() => props.riskScoring);

const validateRiskScoring = () => {
  const errors = {};
  let valid = true;

  for (const exec of riskScoringRuleExecutions.value || []) {
    const skipped = exec.attributes.skipped;
    const justification = exec.attributes.skipReason?.trim();
    if (skipped && !justification) {
      errors[exec.id] = true;
      valid = false;
    }
  }

  validationErrors.value = errors;
  return valid;
};

watch(() => riskScoring.value, (newValue, oldValue) => {
  if (newValue?.id !== oldValue?.id) {
    resetBaseRiskScoringComparison();
  };
  hasChanges.value = !(baseRiskScoringString.value === JSON.stringify(newValue.relationships.riskScoringRuleExecutions));
}, { deep: true });

const recomputeRiskScoring = async () => {
   if (!validateRiskScoring()) {
    return;
  }
  const riskScoringRuleExecutions = riskScoring.value?.relationships?.riskScoringRuleExecutions?.data || [];
  showRiskModal.value = true;
  if (riskScoringOwnerId && riskScoringRuleExecutions.length > 0) {
    await riskScoringStore.skipRiskScoringRuleExecutions(riskScoringRuleExecutions);
    resetBaseRiskScoringComparison();

    setTimeout(() => {
      riskScoringStore.trigger(() => {
        showRiskModal.value = false;
        emit("updated");
      }, () => {
        showRiskModal.value = false;
      });
    }, 500);
  }
};

const confirmRiskScoring = async () => {
  if (confirming.value || !riskScoring.value?.id) {
    return;
  }
  if (!validateRiskScoring()) {
    return;
  }
  if (props.requireHighRiskConfirmation && riskScoring.value?.attributes?.scoreLevel === "high") {
    showHighRiskDialog.value = true;
    return;
  }
  await performConfirmation();
};

const performConfirmation = async () => {
  confirming.value = true;
  try {
    await riskScoringStore.confirm(riskScoring.value.id);
    emit("updated", riskScoring.value);
  } finally {
    confirming.value = false;
  }
};

const onHighRiskConfirm = async (payload) => {
  showHighRiskDialog.value = false;
  if (payload?.amlOfficerEmail && props.organization?.id) {
    await organizationStore.update(props.organization.id, { amlOfficerEmail: payload.amlOfficerEmail });
  }
  await performConfirmation();
};

const resetBaseRiskScoringComparison = () => {
  if (!riskScoring.value?.relationships?.riskScoringRuleExecutions) {
    baseRiskScoringString.value = null;
    hasChanges.value = false;
    return;
  }
  baseRiskScoringString.value = JSON.stringify(riskScoring.value.relationships.riskScoringRuleExecutions);
  hasChanges.value = false;
};

onMounted(() => {
  resetBaseRiskScoringComparison();
});

</script>
